/**
 * AI Answer Generator page.
 *
 * Flow:
 * 1. Select company, role, level, question
 * 2. Enter bullet points for each STAR component
 * 3. AI generates a polished narrative
 * 4. User reviews and edits the draft
 * 5. Submit edited version for evaluation
 *
 * The bullet-point → narrative step uses a separate Claude call
 * with a different prompt and higher temperature than evaluation.
 */

import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";

import {
  listCompanies,
  listQuestions,
  generateAnswer,
  createAnswer,
  getAnswer,
  createEvaluation,
} from "../api/client";
import UpgradePrompt, { isTierLimitError } from "../components/UpgradePrompt";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

const ROLES = ["MLE", "PM", "TPM", "EM"];
const LEVELS = [
  { value: "entry", labelKey: "AnswerGenerator.levelEntryLabel" },
  { value: "mid", labelKey: "AnswerGenerator.levelMidLabel" },
  { value: "senior", labelKey: "AnswerGenerator.levelSeniorLabel" },
  { value: "staff", labelKey: "AnswerGenerator.levelStaffLabel" },
];

type Phase = "input" | "generating" | "editing" | "submitting";

export default function AnswerGenerator() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("pages-core");
  const passedQuestionId = (location.state as any)?.questionId || "";

  // Context
  const [companyId, setCompanyId] = useState("");
  const [role, setRole] = useState("");
  const [level, setLevel] = useState("");
  const [questionId, setQuestionId] = useState(passedQuestionId);
  const [customQuestion, setCustomQuestion] = useState("");

  // Bullet inputs
  const [situation, setSituation] = useState("");
  const [task, setTask] = useState("");
  const [action, setAction] = useState("");
  const [result, setResult] = useState("");

  // Generated output
  const [phase, setPhase] = useState<Phase>("input");
  const [generatedText, setGeneratedText] = useState("");
  const [editedText, setEditedText] = useState("");
  const [genStats, setGenStats] = useState<{
    word_count: number;
    processing_seconds: number;
  } | null>(null);
  const [error, setError] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [tierLimitInfo, setTierLimitInfo] = useState<{
    currentUsage?: number;
    limit?: number;
  }>({});

  // Idempotency: once a submit step succeeds we store its id so a retry after a
  // later-step failure reuses it instead of double-creating the answer / burning
  // the monthly eval quota again. Reset on "Start Over" / a fresh generation.
  const answerIdRef = useRef<string | null>(null);
  const versionIdRef = useRef<string | null>(null);

  const {
    data: companies,
    isLoading: loadingCompanies,
    isError: companiesError,
  } = useQuery({
    queryKey: ["companies"],
    queryFn: listCompanies,
  });

  const {
    data: questions,
    isLoading: loadingQuestions,
    isError: questionsError,
  } = useQuery({
    queryKey: ["questions", role, level],
    queryFn: () =>
      listQuestions({
        role: role || undefined,
        level: level || undefined,
        limit: 100,
      }),
    enabled: true,
  });

  const selectedCompany = companies?.find((c) => c.id === companyId);
  const selectedQuestion = questions?.items.find((q) => q.id === questionId);
  const questionText =
    selectedQuestion?.question_text || customQuestion || "";

  const canGenerate =
    companyId &&
    role &&
    level &&
    questionText.length >= 10 &&
    situation.trim().length >= 5 &&
    task.trim().length >= 5 &&
    action.trim().length >= 5 &&
    result.trim().length >= 5;

  const handleGenerate = async () => {
    if (!selectedCompany) return;
    setError("");
    // Fresh narrative → a fresh answer record; clear any prior idempotency ids.
    answerIdRef.current = null;
    versionIdRef.current = null;
    setPhase("generating");

    try {
      const res = await generateAnswer({
        question_text: questionText,
        company_name: selectedCompany.name,
        target_role: role,
        experience_level: level,
        situation_bullets: situation,
        task_bullets: task,
        action_bullets: action,
        result_bullets: result,
      });

      setGeneratedText(res.answer_text);
      setEditedText(res.answer_text);
      setGenStats({
        word_count: res.word_count,
        processing_seconds: res.processing_seconds,
      });
      setPhase("editing");
    } catch (err: any) {
      setError(
        toUserSafeErrorMessage(err, t("AnswerGenerator.generateFailedError"))
      );
      setPhase("input");
    }
  };

  const handleSubmitForEvaluation = async () => {
    setError("");
    setPhase("submitting");

    try {
      // Step 1 — Save the answer (POST returns metadata only, no versions).
      // Reuse the previously created answer on retry so we never double-create.
      if (!answerIdRef.current) {
        const created = await createAnswer({
          question_id: questionId || undefined,
          custom_question_text: questionId ? undefined : customQuestion,
          target_company_id: companyId,
          target_role: role,
          experience_level: level,
          answer_text: editedText,
          is_ai_assisted: true,
        });
        answerIdRef.current = created.id;
      }

      // Step 2 — Fetch full answer with versions to get the version ID.
      if (!versionIdRef.current) {
        const answer = await getAnswer(answerIdRef.current);
        const versionId =
          answer.versions && answer.versions.length > 0
            ? answer.versions[0].id
            : null;

        if (!versionId) {
          setError(t("AnswerGenerator.retrieveVersionFailedError"));
          setPhase("editing");
          return;
        }
        versionIdRef.current = versionId;
      }

      // Step 3 — Kick off evaluation (the quota-consuming step).
      const evaluation = await createEvaluation(versionIdRef.current);
      navigate(`/evaluations/${evaluation.id}`);
    } catch (err: any) {
      const tierCheck = isTierLimitError(err);
      if (tierCheck.isLimit) {
        setTierLimitInfo({
          currentUsage: tierCheck.currentUsage,
          limit: tierCheck.limit,
        });
        setShowUpgrade(true);
        // Restore the editable state so the user's edited draft isn't trapped
        // behind a frozen "Submitting…" UI after dismissing the dialog.
        setPhase("editing");
        return;
      }
      setError(toUserSafeErrorMessage(err, t("AnswerGenerator.submitFailedError")));
      setPhase("editing");
    }
  };

  const wordCount = editedText.trim().split(/\s+/).filter(Boolean).length;

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: "bottom" }} />
        {t("AnswerGenerator.pageTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("AnswerGenerator.pageSubtitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ---- Phase: Input (bullet points) ---- */}
      {(phase === "input" || phase === "generating") && (
        <>
          {/* Context section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("AnswerGenerator.interviewContextHeading")}
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth error={companiesError}>
                  <InputLabel>{t("AnswerGenerator.targetCompanyLabel")}</InputLabel>
                  <Select
                    value={companyId}
                    label={t("AnswerGenerator.targetCompanyLabel")}
                    onChange={(e) => setCompanyId(e.target.value)}
                    disabled={loadingCompanies}
                  >
                    {companies?.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {companiesError && (
                    <FormHelperText error>
                      {t("AnswerGenerator.companiesLoadError")}
                    </FormHelperText>
                  )}
                  {!loadingCompanies &&
                    !companiesError &&
                    !companies?.length && (
                      <FormHelperText>
                        {t("AnswerGenerator.noCompaniesAvailable")}
                      </FormHelperText>
                    )}
                </FormControl>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel>{t("AnswerGenerator.roleLabel")}</InputLabel>
                    <Select
                      value={role}
                      label={t("AnswerGenerator.roleLabel")}
                      onChange={(e) => setRole(e.target.value)}
                    >
                      {ROLES.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t("AnswerGenerator.levelLabel")}</InputLabel>
                    <Select
                      value={level}
                      label={t("AnswerGenerator.levelLabel")}
                      onChange={(e) => setLevel(e.target.value)}
                    >
                      {LEVELS.map((l) => (
                        <MenuItem key={l.value} value={l.value}>
                          {t(l.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Question selection */}
                <FormControl fullWidth error={questionsError}>
                  <InputLabel>{t("AnswerGenerator.selectQuestionLabel")}</InputLabel>
                  <Select
                    value={questionId}
                    label={t("AnswerGenerator.selectQuestionLabel")}
                    onChange={(e) => {
                      setQuestionId(e.target.value);
                      setCustomQuestion("");
                    }}
                    disabled={loadingQuestions}
                  >
                    <MenuItem value="">
                      <em>{t("AnswerGenerator.typeCustomQuestionOption")}</em>
                    </MenuItem>
                    {questions?.items.map((q) => (
                      <MenuItem key={q.id} value={q.id}>
                        {q.question_text}
                      </MenuItem>
                    ))}
                  </Select>
                  {questionsError && (
                    <FormHelperText error>
                      {t("AnswerGenerator.questionsLoadError")}
                    </FormHelperText>
                  )}
                  {!loadingQuestions &&
                    !questionsError &&
                    !questions?.items?.length && (
                      <FormHelperText>
                        {t("AnswerGenerator.noQuestionsAvailable")}
                      </FormHelperText>
                    )}
                </FormControl>

                {!questionId && (
                  <TextField
                    fullWidth
                    label={t("AnswerGenerator.customQuestionLabel")}
                    placeholder={t("AnswerGenerator.customQuestionPlaceholder")}
                    value={customQuestion}
                    onChange={(e) => setCustomQuestion(e.target.value)}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* STAR bullet inputs */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("AnswerGenerator.experienceBulletsHeading")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {t("AnswerGenerator.experienceBulletsIntro")}
              </Typography>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("AnswerGenerator.situationLabel")}
                  placeholder={t("AnswerGenerator.situationPlaceholder")}
                  value={situation}
                  onChange={(e) => setSituation(e.target.value)}
                  helperText={t("AnswerGenerator.situationHelperText")}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={t("AnswerGenerator.taskLabel")}
                  placeholder={t("AnswerGenerator.taskPlaceholder")}
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  helperText={t("AnswerGenerator.taskHelperText")}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("AnswerGenerator.actionLabel")}
                  placeholder={t("AnswerGenerator.actionPlaceholder")}
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  helperText={t("AnswerGenerator.actionHelperText")}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={t("AnswerGenerator.resultLabel")}
                  placeholder={t("AnswerGenerator.resultPlaceholder")}
                  value={result}
                  onChange={(e) => setResult(e.target.value)}
                  helperText={t("AnswerGenerator.resultHelperText")}
                />
              </Stack>
            </CardContent>
          </Card>

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={
              phase === "generating" ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
            onClick={handleGenerate}
            disabled={!canGenerate || phase === "generating"}
          >
            {phase === "generating"
              ? t("AnswerGenerator.generatingButtonBusy")
              : t("AnswerGenerator.generateStarAnswerButton")}
          </Button>
        </>
      )}

      {/* ---- Phase: Editing (review generated answer) ---- */}
      {(phase === "editing" || phase === "submitting") && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <EditIcon color="primary" />
                <Typography variant="h6">{t("AnswerGenerator.reviewEditHeading")}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {genStats && (
                  <Chip
                    label={t("AnswerGenerator.aiGeneratedInSeconds", { seconds: genStats.processing_seconds })}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {t("AnswerGenerator.reviewEditIntro")}
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={10}
                maxRows={20}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                disabled={phase === "submitting"}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("AnswerGenerator.wordCount", { count: wordCount })}
                </Typography>
                {wordCount < 100 && (
                  <Typography variant="body2" color="warning.main">
                    {t("AnswerGenerator.aimForWordCount")}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Context reminder */}
          <Card sx={{ mb: 3, bgcolor: "grey.50" }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{selectedCompany?.name}</strong> &middot; {role} &middot;{" "}
                {level}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {questionText}
              </Typography>
            </CardContent>
          </Card>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setPhase("input");
                setGeneratedText("");
                setEditedText("");
                setGenStats(null);
                // New attempt — drop the idempotency ids so the next submit
                // creates a fresh answer rather than reusing a stale one.
                answerIdRef.current = null;
                versionIdRef.current = null;
              }}
            >
              {t("AnswerGenerator.startOverButton")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setEditedText(generatedText)}
              disabled={editedText === generatedText}
            >
              {t("AnswerGenerator.resetToOriginalButton")}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              endIcon={
                phase === "submitting" ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              onClick={handleSubmitForEvaluation}
              disabled={
                phase === "submitting" || editedText.trim().length < 10
              }
            >
              {phase === "submitting"
                ? t("AnswerGenerator.submittingButtonBusy")
                : t("AnswerGenerator.submitForEvaluationButton")}
            </Button>
          </Box>
        </>
      )}

      {/* Upgrade prompt when tier limit is reached */}
      <UpgradePrompt
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentUsage={tierLimitInfo.currentUsage}
        limit={tierLimitInfo.limit}
      />
    </Box>
  );
}
